import { useChartColors } from '../lib/accessibility'
import { apiError } from '../lib/errors'
import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts'
import { Plus, Wheat, X } from 'lucide-react'
import { harvestsApi, varietiesApi, prefsApi, vendorsApi, pricingApi, type HarvestEvent, type UserPreferences, type SaleInput } from '../lib/api'
import { formatWeight, weightUnit, inputToKg, type MeasurementSystem } from '../lib/units'

// ── Design tokens ───────────────────────────────────────────────────────────
const C = {
  bg:        'var(--color-bg)',
  card:      'var(--color-bg-card)',
  card2:     'var(--color-bg-card-2)',
  border:    'var(--color-border)',
  amber:     'var(--color-primary)',
  green:     '#4CAF50',
  red:       '#F87171',
  blue:      '#60A5FA',
  text:      'var(--color-text)',
  textMuted: 'var(--color-text-muted)',
  textFaint: 'var(--color-text-faint)',
}

const inp: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', background: C.card2,
  border: `1px solid ${C.border}`, borderRadius: 8,
  padding: '8px 12px', color: C.text, fontSize: 13, outline: 'none',
}
const lbl: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted,
  marginBottom: 4, textTransform: 'uppercase' as const, letterSpacing: '0.06em',
}

const GRADES = ['A', 'B', 'C', 'Cull', 'Export']
const CHART_COLORS = [C.amber, C.green, C.blue, '#A78BFA', '#2DD4BF', '#FB923C', '#E879F9', '#34D399']

const tooltipStyle = {
  background: C.card2, border: `1px solid ${C.border}`,
  borderRadius: 8, fontSize: 12, color: C.text,
}

function gradeColor(g?: string) {
  if (g === 'A' || g === 'Export') return C.green
  if (g === 'B') return C.amber
  if (g === 'C') return C.blue
  if (g === 'Cull') return C.red
  return C.textMuted
}

// ── Log Harvest Modal ────────────────────────────────────────────────────────
function HarvestModal({ onClose, measurementSystem }: { onClose: () => void; measurementSystem: MeasurementSystem }) {
  const qc = useQueryClient()
  const now = new Date().toISOString().slice(0, 16)
  const [form, setForm] = useState({
    variety_id: '', harvested_at: now, weight: '', unit_count: '',
    grade: 'A', brix: '', season: String(new Date().getFullYear()), notes: '',
  })
  const [error, setError] = useState<string | null>(null)
  const set = <K extends keyof typeof form>(k: K, v: string) => setForm(f => ({ ...f, [k]: v }))

  const { data: varietiesData } = useQuery({
    queryKey: ['varieties', 'all'],
    queryFn: () => varietiesApi.list().then(r => r.data),
  })

  const save = useMutation({
    mutationFn: (data: Partial<HarvestEvent>) => harvestsApi.create(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['harvests'] }); onClose() },
    onError: (e) => setError(apiError(e, 'Failed to save')),
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.weight && !form.unit_count) { setError('Enter weight or unit count'); return }
    save.mutate({
      variety_id: form.variety_id || undefined,
      harvested_at: new Date(form.harvested_at).toISOString(),
      weight_kg: form.weight ? inputToKg(parseFloat(form.weight), measurementSystem) : undefined,
      unit_count: form.unit_count ? parseInt(form.unit_count) : undefined,
      grade: form.grade || undefined,
      brix: form.brix ? parseFloat(form.brix) : undefined,
      season: form.season || undefined,
      notes: form.notes,
    })
  }

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, width: 440, maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${C.border}` }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>Log harvest</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint }}><X size={18} /></button>
        </div>
        <form onSubmit={submit} style={{ padding: 20, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={lbl}>Variety</label>
            <select style={{ ...inp, cursor: 'pointer' }} value={form.variety_id} onChange={e => set('variety_id', e.target.value)}>
              <option value="">— Select variety —</option>
              {(varietiesData?.varieties ?? []).map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Harvested at</label>
            <input style={inp} type="datetime-local" value={form.harvested_at} onChange={e => set('harvested_at', e.target.value)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={lbl}>Weight ({weightUnit(measurementSystem)})</label><input style={inp} type="number" min={0} step={0.1} value={form.weight} onChange={e => set('weight', e.target.value)} placeholder={measurementSystem === 'imperial' ? '56.2' : '25.5'} /></div>
            <div><label style={lbl}>Unit count</label><input style={inp} type="number" min={0} value={form.unit_count} onChange={e => set('unit_count', e.target.value)} placeholder="48" /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Grade</label>
              <select style={{ ...inp, cursor: 'pointer' }} value={form.grade} onChange={e => set('grade', e.target.value)}>
                {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div><label style={lbl}>Brix (°Bx)</label><input style={inp} type="number" min={0} max={40} step={0.1} value={form.brix} onChange={e => set('brix', e.target.value)} placeholder="sugar" /></div>
            <div><label style={lbl}>Season</label><input style={inp} value={form.season} onChange={e => set('season', e.target.value)} placeholder="2025" /></div>
          </div>
          <div><label style={lbl}>Notes</label><textarea style={{ ...inp, resize: 'vertical', minHeight: 64, fontFamily: 'inherit' }} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Weather conditions, field notes…" /></div>
          {error && <div style={{ fontSize: 12, color: C.red, padding: '8px 12px', background: 'rgba(248,113,113,0.1)', borderRadius: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '9px 0', borderRadius: 8, background: C.card2, border: `1px solid ${C.border}`, color: C.textMuted, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            <button type="submit" disabled={save.isPending} style={{ flex: 2, padding: '9px 0', borderRadius: 8, background: C.amber, border: 'none', color: C.bg, cursor: save.isPending ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
              {save.isPending ? 'Saving…' : 'Log harvest'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Allocate / Record Sale Modal ──────────────────────────────────────────────
// Allocate a harvest to a sales channel: retail (farm stand / online / Facebook /
// farmers market / other) or wholesale (to a tracked vendor/partner). Captures
// quantity + unit price; shows the harvest's existing allocations + remaining qty.
const RETAIL_CHANNELS = [
  { value: 'farmstand', label: 'Farm stand' },
  { value: 'farmers_market', label: 'Farmers market' },
  { value: 'online', label: 'Online store' },
  { value: 'facebook', label: 'Facebook / social' },
  { value: 'other', label: 'Other retail' },
]
const channelLabel = (c: string) =>
  c === 'wholesale' ? 'Wholesale' : (RETAIL_CHANNELS.find(r => r.value === c)?.label ?? c)

function SaleModal({ harvest, onClose, measurementSystem }: { harvest: HarvestEvent; onClose: () => void; measurementSystem: MeasurementSystem }) {
  const qc = useQueryClient()
  const unit = weightUnit(measurementSystem)
  const kgPerUnit = inputToKg(1, measurementSystem) // 1 for metric, ~0.4536 for imperial
  const [mode, setMode] = useState<'retail' | 'wholesale'>('wholesale')
  const [form, setForm] = useState({
    channel: 'farmstand', vendor_id: '', quantity: '', unit_price: '',
    sale_date: new Date().toISOString().slice(0, 10), notes: '',
  })
  const [error, setError] = useState<string | null>(null)
  const [priceTouched, setPriceTouched] = useState(false)
  const set = <K extends keyof typeof form>(k: K, v: string) => setForm(f => ({ ...f, [k]: v }))

  const { data: vendorsData } = useQuery({ queryKey: ['vendors'], queryFn: () => vendorsApi.list().then(r => r.data) })
  const { data: salesData } = useQuery({ queryKey: ['harvest-sales', harvest.id], queryFn: () => harvestsApi.listSales(harvest.id).then(r => r.data) })
  const { data: pricesData } = useQuery({ queryKey: ['pricing'], queryFn: () => pricingApi.list().then(r => r.data) })
  const vendors = vendorsData?.vendors ?? []
  const sales = salesData?.sales ?? []

  // The variety default for this harvest (cents PER KG). Used to pre-fill price.
  const vp = pricesData?.prices.find(p => p.variety_id === harvest.variety_id)
  const defaultCentsPerKg = mode === 'retail' ? vp?.retail_cents_per_kg : vp?.wholesale_cents_per_kg
  // cents/kg → $ per display unit (the input is $/unit).
  const suggestedUnitPrice = defaultCentsPerKg != null ? ((defaultCentsPerKg / 100) * kgPerUnit) : null
  // Pre-fill the price from the variety default when the user hasn't typed one
  // (re-runs on Retail/Wholesale switch so each channel shows its own default).
  useEffect(() => {
    if (priceTouched) return
    setForm(f => ({ ...f, unit_price: suggestedUnitPrice != null ? suggestedUnitPrice.toFixed(2) : '' }))
  }, [suggestedUnitPrice, priceTouched])

  const totalKg = harvest.weight_kg ?? 0
  const allocatedKg = sales.reduce((s, x) => s + (x.quantity_kg ?? 0), 0)
  const remainingKg = Math.max(0, totalKg - allocatedKg)
  const saleTotal = (s: { unit_price_cents?: number | null; quantity_kg: number }) =>
    s.unit_price_cents != null ? (s.unit_price_cents / 100) * s.quantity_kg : null

  const save = useMutation({
    mutationFn: (data: SaleInput) => harvestsApi.recordSale(harvest.id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['harvest-sales', harvest.id] })
      qc.invalidateQueries({ queryKey: ['harvests'] })
      setForm(f => ({ ...f, quantity: '', unit_price: '', notes: '' }))
      setPriceTouched(false) // re-prefill the variety default for the next allocation
    },
    onError: (e) => setError(apiError(e, 'Failed to record sale')),
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const qtyKg = form.quantity ? inputToKg(parseFloat(form.quantity), measurementSystem) : 0
    if (!qtyKg) { setError('Enter a quantity to allocate'); return }
    if (mode === 'wholesale' && !form.vendor_id) { setError('Pick a wholesale partner — add one on the Vendors page if needed'); return }
    // unit_price is $ per display unit; harvest_sales stores cents PER KG.
    const unitPriceCents = form.unit_price ? Math.round((parseFloat(form.unit_price) / kgPerUnit) * 100) : undefined
    save.mutate({
      channel: mode === 'wholesale' ? 'wholesale' : form.channel,
      vendor_id: mode === 'wholesale' ? form.vendor_id : undefined,
      quantity_kg: qtyKg,
      unit_price_cents: unitPriceCents,
      sale_date: form.sale_date,
      notes: form.notes || undefined,
    })
  }

  const tabStyle = (active: boolean): React.CSSProperties => ({
    flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
    background: active ? 'color-mix(in srgb, var(--color-primary) 16%, transparent)' : C.card2,
    border: `1px solid ${active ? 'color-mix(in srgb, var(--color-primary) 45%, transparent)' : C.border}`,
    color: active ? C.amber : C.textMuted,
  })

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, width: 460, maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${C.border}` }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>Allocate harvest</h2>
            <div style={{ fontSize: 11, color: C.textFaint, marginTop: 2 }}>
              {harvest.variety_name ? `${harvest.variety_name} · ` : ''}
              {formatWeight(remainingKg, measurementSystem)} of {formatWeight(totalKg, measurementSystem)} unallocated
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint }}><X size={18} /></button>
        </div>
        <div style={{ padding: 20, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" style={tabStyle(mode === 'retail')} onClick={() => setMode('retail')}>Retail</button>
              <button type="button" style={tabStyle(mode === 'wholesale')} onClick={() => setMode('wholesale')}>Wholesale</button>
            </div>
            {mode === 'retail' ? (
              <div>
                <label style={lbl}>Channel</label>
                <select style={{ ...inp, cursor: 'pointer' }} value={form.channel} onChange={e => set('channel', e.target.value)}>
                  {RETAIL_CHANNELS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            ) : (
              <div>
                <label style={lbl}>Wholesale partner</label>
                <select style={{ ...inp, cursor: 'pointer' }} value={form.vendor_id} onChange={e => set('vendor_id', e.target.value)}>
                  <option value="">— Select partner —</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
                {vendors.length === 0 && <div style={{ fontSize: 11, color: C.textFaint, marginTop: 4 }}>No partners yet — add them on the Vendors page.</div>}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={lbl}>Quantity ({unit})</label><input style={inp} type="number" min={0} step={0.1} value={form.quantity} onChange={e => set('quantity', e.target.value)} placeholder={remainingKg ? (remainingKg / kgPerUnit).toFixed(1) : '0'} /></div>
              <div>
                <label style={lbl}>Price / {unit} ($)</label>
                <input style={inp} type="number" min={0} step={0.01} value={form.unit_price} onChange={e => { setPriceTouched(true); set('unit_price', e.target.value) }} placeholder="0.00" />
                {suggestedUnitPrice != null && (
                  <div style={{ fontSize: 10.5, color: C.textFaint, marginTop: 3 }}>
                    {priceTouched
                      ? <button type="button" onClick={() => { setPriceTouched(false) }} style={{ background: 'none', border: 'none', padding: 0, color: C.amber, cursor: 'pointer', fontSize: 10.5 }}>Use ${suggestedUnitPrice.toFixed(2)}/{unit} default</button>
                      : <>Variety {mode} default{vp && (mode === 'retail' ? vp.retail_source : vp.wholesale_source) === 'ai_estimate' ? ' · auto' : ''}</>}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={lbl}>Sale date</label><input style={inp} type="date" value={form.sale_date} onChange={e => set('sale_date', e.target.value)} /></div>
              <div><label style={lbl}>Notes</label><input style={inp} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="PO #, terms…" /></div>
            </div>
            {error && <div style={{ fontSize: 12, color: C.red, padding: '8px 12px', background: 'rgba(248,113,113,0.1)', borderRadius: 8 }}>{error}</div>}
            <button type="submit" disabled={save.isPending} style={{ padding: '9px 0', borderRadius: 8, background: C.amber, border: 'none', color: C.bg, cursor: save.isPending ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
              {save.isPending ? 'Recording…' : 'Record sale'}
            </button>
          </form>

          {sales.length > 0 && (
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Allocations</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {sales.map(s => {
                  const tot = saleTotal(s)
                  return (
                    <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: C.textMuted }}>
                      <span>
                        <span style={{ color: C.text, fontWeight: 600 }}>{channelLabel(s.channel)}</span>
                        {s.vendor_name ? ` · ${s.vendor_name}` : ''} · {formatWeight(s.quantity_kg, measurementSystem)}
                      </span>
                      <span style={{ color: C.text }}>{tot != null ? `$${tot.toFixed(2)}` : '—'}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Main Page ────────────────────────────────────────────────────────────────
export function HarvestPage() {
  const chartC = useChartColors()
  const [showModal, setShowModal] = useState(false)
  const [saleHarvest, setSaleHarvest] = useState<HarvestEvent | null>(null)
  const [filterSeason, setFilterSeason] = useState('')
  const [chartView, setChartView]       = useState<'weight' | 'revenue'>('weight')

  const { data: prefsData } = useQuery({
    queryKey: ['user-preferences'],
    queryFn: (): Promise<UserPreferences> => prefsApi.get().then(r => r.data),
    staleTime: 5 * 60_000,
  })
  const measurementSystem: MeasurementSystem = prefsData?.measurement_system ?? 'imperial'

  const { data, isLoading } = useQuery({
    queryKey: ['harvests', filterSeason],
    queryFn: () => harvestsApi.list(filterSeason ? { season: filterSeason } : undefined).then(r => r.data),
  })
  const { data: summaryRaw } = useQuery({
    queryKey: ['harvest-summary-variety'],
    queryFn: () => harvestsApi.summaryByVariety().then(r => r.data),
  })
  useQuery({
    queryKey: ['harvest-summary-season'],
    queryFn: () => harvestsApi.summaryBySeason().then(r => r.data),
  })

  const harvests  = data?.harvests ?? []
  const summary   = summaryRaw ?? []
  const seasons   = [...new Set(harvests.map(h => h.season).filter(Boolean))]
  const totalKg   = harvests.reduce((s, h) => s + (h.weight_kg ?? 0), 0)
  const totalRev  = summary.reduce((s, v) => s + (v.total_revenue ?? 0), 0)
  const totalUnits = harvests.reduce((s, h) => s + (h.unit_count ?? 0), 0)

  const chartData = summary
    .filter(s => chartView === 'weight' ? s.total_kg > 0 : s.total_revenue > 0)
    .sort((a, b) => chartView === 'weight' ? b.total_kg - a.total_kg : b.total_revenue - a.total_revenue)
    .slice(0, 12)

  return (
    <main id="main-content" style={{ padding: 32, fontFamily: '"DM Sans", system-ui, sans-serif', background: C.bg, minHeight: '100dvh', color: C.text }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, fontFamily: '"Syne", system-ui', color: C.text }}>Harvest</h1>
          <p style={{ margin: '3px 0 0', fontSize: 13, color: C.textMuted }}>Weight, count, grade, and season records</p>
        </div>
        <button onClick={() => setShowModal(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 9, background: C.amber, border: 'none', color: C.bg, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          <Plus size={16} /> Log harvest
        </button>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 24 }}>
        {[
          { label: 'Total batches',    value: harvests.length,        accent: undefined },
          { label: 'Total weight',     value: formatWeight(totalKg, measurementSystem), accent: C.amber },
          { label: 'Total units',      value: totalUnits > 0 ? totalUnits : undefined, accent: undefined },
          { label: 'Varieties',        value: summary.length,         accent: undefined },
          { label: 'Revenue',          value: totalRev > 0 ? `$${totalRev.toFixed(0)}` : undefined, accent: C.green },
        ].map(s => s.value !== undefined && (
          <div key={s.label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.accent ?? C.text }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Variety chart */}
      {summary.length > 0 && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              Yield by variety
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {(['weight', 'revenue'] as const).map(v => (
                <button key={v} onClick={() => setChartView(v)} style={{
                  padding: '5px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600,
                  background: chartView === v ? 'color-mix(in srgb, var(--color-primary) 13%, transparent)' : C.card2,
                  color: chartView === v ? C.amber : C.textMuted,
                  border: `1px solid ${chartView === v ? 'color-mix(in srgb, var(--color-primary) 33%, transparent)' : C.border}`,
                  cursor: 'pointer',
                }}>
                  {v === 'weight' ? 'By weight' : 'By revenue'}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 40, bottom: 0, left: 80 }}>
              <XAxis type="number" tick={{ fill: chartC.faint, fontSize: 11 }} axisLine={false} tickLine={false}
                unit={chartView === 'weight' ? ` ${weightUnit(measurementSystem)}` : ''}
                tickFormatter={chartView === 'revenue' ? v => `$${v}` : undefined} />
              <YAxis type="category" dataKey="variety_name" tick={{ fill: chartC.muted, fontSize: 12 }} axisLine={false} tickLine={false} width={76} />
              <Tooltip contentStyle={tooltipStyle}
                formatter={(v: number) => chartView === 'weight'
                  ? [formatWeight(v, measurementSystem), 'Yield']
                  : [`$${v.toFixed(0)}`, 'Revenue']} />
              <Bar dataKey={chartView === 'weight' ? 'total_kg' : 'total_revenue'} radius={[0, 4, 4, 0]}>
                {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Season filter */}
      {seasons.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
          <button onClick={() => setFilterSeason('')} style={{ padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: !filterSeason ? 600 : 400, cursor: 'pointer', background: !filterSeason ? C.amber : C.card, color: !filterSeason ? C.bg : C.textMuted, border: `1px solid ${!filterSeason ? C.amber : C.border}` }}>All seasons</button>
          {seasons.map(s => (
            <button key={s} onClick={() => setFilterSeason(s!)} style={{ padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: filterSeason === s ? 600 : 400, cursor: 'pointer', background: filterSeason === s ? C.amber : C.card, color: filterSeason === s ? C.bg : C.textMuted, border: `1px solid ${filterSeason === s ? C.amber : C.border}` }}>{s}</button>
          ))}
        </div>
      )}

      {/* Harvest list */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: C.textFaint, fontSize: 14 }}>Loading…</div>
      ) : harvests.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', border: `2px dashed ${C.border}`, borderRadius: 12 }}>
          <Wheat size={32} style={{ color: C.textFaint, marginBottom: 12, opacity: 0.5 }} />
          <p style={{ fontSize: 14, color: C.textMuted, margin: 0 }}>No harvest records yet.</p>
          <p style={{ fontSize: 12, color: C.textFaint, margin: '6px 0 16px' }}>Log your first harvest to start tracking yields, grades, and revenue.</p>
          <button onClick={() => setShowModal(true)} style={{ padding: '9px 18px', borderRadius: 8, background: C.amber, border: 'none', color: C.bg, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Log first harvest</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {harvests.map(h => (
            <div key={h.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                  {h.grade && (
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: gradeColor(h.grade) + '22', color: gradeColor(h.grade), border: `1px solid ${gradeColor(h.grade)}44` }}>
                      Grade {h.grade}
                    </span>
                  )}
                  {h.variety_name && <span style={{ fontSize: 12, color: C.textMuted }}>{h.variety_name}</span>}
                  {h.brix != null && (
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: '#2D6A2F22', color: '#2D6A2F', border: '1px solid #2D6A2F44' }} title="Sugar content (Brix)">
                      {h.brix}°Bx
                    </span>
                  )}
                  {h.season && <span style={{ fontSize: 11, color: C.textFaint }}>Season {h.season}</span>}
                </div>
                {h.notes && <p style={{ margin: '2px 0 0', fontSize: 12, color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.notes}</p>}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                {h.weight_kg != null && <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{formatWeight(h.weight_kg, measurementSystem)}</div>}
                {h.unit_count != null && <div style={{ fontSize: 12, color: C.textMuted }}>{h.unit_count} units</div>}
                <div style={{ fontSize: 11, color: C.textFaint, marginTop: 2 }}>
                  {new Date(h.harvested_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
              </div>
              <button
                onClick={() => setSaleHarvest(h)}
                title="Allocate this harvest to retail or wholesale"
                style={{ flexShrink: 0, padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: C.amber, background: 'color-mix(in srgb, var(--color-primary) 14%, transparent)', border: '1px solid color-mix(in srgb, var(--color-primary) 40%, transparent)' }}>
                Sell
              </button>
            </div>
          ))}
        </div>
      )}

      {showModal && <HarvestModal onClose={() => setShowModal(false)} measurementSystem={measurementSystem} />}
      {saleHarvest && <SaleModal harvest={saleHarvest} onClose={() => setSaleHarvest(null)} measurementSystem={measurementSystem} />}
    </main>
  )
}
